import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { getQueuesAPI } from '../../util';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine
} from 'recharts';

// ============================================================================
// CHART HELPERS
// ============================================================================

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const parts = timestamp.split('T');
  if (parts.length < 2) return timestamp;
  const [hour, minute] = parts[1].split(':');
  const h = parseInt(hour, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${minute} ${ampm}`;
};

const formatDwellTime = (seconds) => {
  if (!seconds && seconds !== 0) return '--';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const getCapacityColor = (pct) => {
  if (pct >= 0.9) return '#dc3545';
  if (pct >= 0.7) return '#fd7e14';
  if (pct >= 0.5) return '#ffc107';
  return '#28a745';
};

const chartContainerStyle = {
  marginBottom: "24px",
};

const chartTitleStyle = {
  fontSize: "15px",
  fontWeight: "600",
  color: "#333",
  marginBottom: "4px",
};

const chartSubtitleStyle = {
  fontSize: "12px",
  color: "#999",
  marginBottom: "12px",
};

const thinData = (data, maxPoints = 200) => {
  if (!data || data.length <= maxPoints) return data;
  const step = Math.ceil(data.length / maxPoints);
  return data.filter((_, i) => i % step === 0 || i === data.length - 1);
};

// ============================================================================
// OCCUPANCY CHART
// ============================================================================

const OccupancyChart = ({ data, maxValue }) => {
  const chartData = useMemo(() => {
    if (!data?.length) return [];
    return thinData(data).map(d => ({
      time: formatTime(d.timestamp),
      rawTime: d.timestamp,
      value: d.value,
      percentage: d.percentage,
      maxValue: d.maxValue || maxValue,
    }));
  }, [data, maxValue]);

  if (!chartData.length) return null;

  const peakPoint = chartData.reduce((max, d) => d.value > max.value ? d : max, chartData[0]);

  return (
    <div style={chartContainerStyle}>
      <div style={chartTitleStyle}>Occupancy Over Time</div>
      <div style={chartSubtitleStyle}>
        Peak: {peakPoint.value.toLocaleString()} ({Math.round(peakPoint.percentage * 100)}%) at {peakPoint.time}
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="occGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#007bff" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#007bff" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 11, fill: '#999' }}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={60}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#999' }}
            tickLine={false}
            axisLine={false}
            domain={[0, maxValue]}
          />
          <Tooltip
            contentStyle={{ borderRadius: '8px', border: '1px solid #eee', fontSize: '13px' }}
            formatter={(val, name) => [
              `${val.toLocaleString()} / ${maxValue.toLocaleString()} (${Math.round((val / maxValue) * 100)}%)`,
              'Occupancy'
            ]}
          />
          <ReferenceLine y={maxValue} stroke="#dc3545" strokeDasharray="4 4" strokeOpacity={0.5} />
          <Area
            type="monotone"
            dataKey="value"
            stroke="#007bff"
            strokeWidth={2}
            fill="url(#occGrad)"
            dot={false}
            activeDot={{ r: 5, fill: '#007bff', stroke: '#007bff', strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// ============================================================================
// THROUGHPUT CHART
// ============================================================================

const ThroughputChart = ({ data }) => {
  const chartData = useMemo(() => {
    if (!data?.length) return [];
    return thinData(data).map(d => ({
      time: formatTime(d.timestamp),
      rawTime: d.timestamp,
      count: d.count,
    }));
  }, [data]);

  const peakRate = useMemo(() => {
    if (!data || data.length < 2) return null;
    const windowSize = Math.min(10, Math.max(2, Math.floor(data.length / 4)));
    let maxDelta = 0;
    let maxIdx = -1;
    for (let i = windowSize; i < data.length; i++) {
      const delta = data[i].count - data[i - windowSize].count;
      if (delta > maxDelta) {
        maxDelta = delta;
        maxIdx = i;
      }
    }
    if (maxIdx === -1) return null;
    return {
      startTime: formatTime(data[maxIdx - windowSize].timestamp),
      endTime: formatTime(data[maxIdx].timestamp),
      entries: maxDelta,
      windowSize,
    };
  }, [data]);

  if (!chartData.length) return null;

  const latestCount = chartData[chartData.length - 1].count;

  return (
    <div style={chartContainerStyle}>
      <div style={chartTitleStyle}>Throughput (Cumulative Entries)</div>
      <div style={chartSubtitleStyle}>
        Total: {latestCount.toLocaleString()}
        {peakRate && ` · Peak: ${peakRate.entries.toLocaleString()} entries from ${peakRate.startTime} to ${peakRate.endTime}`}
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="thruGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#17a2b8" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#17a2b8" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 11, fill: '#999' }}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={60}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#999' }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            contentStyle={{ borderRadius: '8px', border: '1px solid #eee', fontSize: '13px' }}
            formatter={(val) => [`${val.toLocaleString()} entries`, 'Throughput']}
          />
          <Area
            type="monotone"
            dataKey="count"
            stroke="#17a2b8"
            strokeWidth={2}
            fill="url(#thruGrad)"
            dot={false}
            activeDot={{ r: 5, fill: '#17a2b8', stroke: '#17a2b8', strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// ============================================================================
// DWELL TIME CHART
// ============================================================================

const DwellTimeChart = ({ dwellTimeData }) => {
  const chartData = useMemo(() => {
    if (!dwellTimeData?.data?.length) return [];
    return thinData(dwellTimeData.data).map(d => ({
      time: formatTime(d.timestamp),
      rawTime: d.timestamp,
      seconds: d.time,
      minutes: Math.round((d.time / 60) * 10) / 10,
    }));
  }, [dwellTimeData]);

  if (!chartData.length) return null;

  const avgSeconds = Math.round(
    chartData.reduce((sum, d) => sum + d.seconds, 0) / chartData.length
  );
  const peakPoint = chartData.reduce((max, d) => d.seconds > max.seconds ? d : max, chartData[0]);

  return (
    <div style={chartContainerStyle}>
      <div style={chartTitleStyle}>Avg Dwell Time — {dwellTimeData.area}</div>
      <div style={chartSubtitleStyle}>
        Avg: {formatDwellTime(avgSeconds)} · Peak: {formatDwellTime(peakPoint.seconds)} at {peakPoint.time}
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 11, fill: '#999' }}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={60}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#999' }}
            tickLine={false}
            axisLine={false}
            width={55}
            tickFormatter={(v) => {
              if (v < 60) return `${v}s`;
              const m = Math.floor(v / 60);
              const s = Math.round(v % 60);
              return s === 0 ? `${m}m` : `${m}m ${s}s`;
            }}
          />
          <Tooltip
            contentStyle={{ borderRadius: '8px', border: '1px solid #eee', fontSize: '13px' }}
            formatter={(val) => [formatDwellTime(val), 'Dwell Time']}
            labelFormatter={(label) => label}
          />
          <ReferenceLine
            y={avgSeconds}
            stroke="#fd7e14"
            strokeDasharray="4 4"
            strokeOpacity={0.6}
          />
          <Line
            type="monotone"
            dataKey="seconds"
            stroke="#fd7e14"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, fill: '#fd7e14', stroke: '#fd7e14', strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// ============================================================================
// GATE THROUGHPUT CHART
// ============================================================================

const GateThroughputChart = ({ data }) => {
  const chartData = useMemo(() => {
    if (!data?.length) return [];
    return thinData(data).map(d => ({
      time: formatTime(d.timestamp),
      rawTime: d.timestamp,
      countOut: d.countOut,
    }));
  }, [data]);

  if (!chartData.length) return null;

  const latestCount = chartData[chartData.length - 1].countOut;

  return (
    <div style={chartContainerStyle}>
      <div style={chartTitleStyle}>Gate Exits (Cumulative)</div>
      <div style={chartSubtitleStyle}>
        Total: {latestCount.toLocaleString()} exits
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="gateGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6f42c1" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#6f42c1" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 11, fill: '#999' }}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={60}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#999' }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            contentStyle={{ borderRadius: '8px', border: '1px solid #eee', fontSize: '13px' }}
            formatter={(val) => [`${val.toLocaleString()} exits`, 'Gate Exits']}
          />
          <Area
            type="monotone"
            dataKey="countOut"
            stroke="#6f42c1"
            strokeWidth={2}
            fill="url(#gateGrad)"
            dot={false}
            activeDot={{ r: 5, fill: '#6f42c1', stroke: '#6f42c1', strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// ============================================================================
// OCCUPANCY BUFFER CONTROL
// ============================================================================

const getBufferOptions = (maxValue) => {
  if (!maxValue || maxValue <= 0) return [0];
  return [
    0,
    Math.round(maxValue * 0.25),
    Math.round(maxValue * 0.50),
    Math.round(maxValue * 0.75),
    maxValue,
  ];
};

const OccupancyBufferControl = ({ location, locationData, authCtx }) => {
  const [activeBuffer, setActiveBuffer] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    getQueuesAPI("get-golf-event-location-buffer",
      {
        measurement: locationData.measurement,
        org: authCtx.org,
        locationId: location.locationId,
      },
      (data, error) => {
        if (error) {
          setActiveBuffer(0);
        } else {
          setActiveBuffer(data?.occupancyBuffer ?? 0);
        }
      },
      authCtx.sessionToken
    );
  }, [location.locationId, locationData.measurement, authCtx.org, authCtx.sessionToken]);

  const handleSetBuffer = useCallback((value) => {
    if (value === activeBuffer) return;
    setUpdating(true);

    getQueuesAPI("update-golf-event-location-buffer",
      {
        measurement: locationData.measurement,
        org: authCtx.org,
        locationId: location.locationId,
        buffer: value,
      },
      (data, error) => {
        setUpdating(false);
        if (!error) {
          setActiveBuffer(value);
        }
      },
      authCtx.sessionToken
    );
  }, [activeBuffer, location.locationId, locationData.measurement, authCtx.org, authCtx.sessionToken]);

  if (activeBuffer === null) return null;

  return (
    <div style={{
      borderRadius: "12px",
      backgroundColor: "#f8f9fa",
      padding: "20px 24px",
      marginBottom: "24px",
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "12px",
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ fontSize: "15px", fontWeight: "600", color: "#333" }}>
              Occupancy Buffer
            </div>
            {activeBuffer > 0 && (
              <div style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#e20074",
                backgroundColor: "#fce4f0",
                padding: "2px 10px",
                borderRadius: "10px",
              }}>
                +{activeBuffer} active
              </div>
            )}
          </div>
          <div style={{ fontSize: "13px", color: "#888", marginTop: "2px" }}>
            Adds a safety buffer to the vendor-facing occupancy count
          </div>
        </div>

        <div style={{ display: "flex", gap: "6px" }}>
          {getBufferOptions(location.maxValue).map((val, i) => {
            const pct = i === 0 ? null : i * 25;
            return (
              <button
                key={val}
                onClick={() => handleSetBuffer(val)}
                disabled={updating}
                style={{
                  fontSize: "13px",
                  fontWeight: "500",
                  padding: "6px 14px",
                  borderRadius: "8px",
                  border: activeBuffer === val ? "2px solid #e20074" : "1px solid #dee2e6",
                  backgroundColor: activeBuffer === val ? "#fce4f0" : "#fff",
                  color: activeBuffer === val ? "#e20074" : "#555",
                  cursor: updating ? "not-allowed" : "pointer",
                  opacity: updating ? 0.6 : 1,
                  transition: "all 0.15s ease",
                }}
              >
                {val === 0 ? "Off" : `+${pct}% (${val})`}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function GolfLocationDetail({ location, locationData, currentData, dailyData, detailLoading, isToday, onClose, authCtx }) {
  const isGate = location.type === 'gate';
  const value = currentData?.value || 0;
  const maxValue = location.maxValue || 0;
  const percentage = currentData?.percentage || 0;
  const percentDisplay = Math.round(percentage * 100);

  // Check if current user is admin for this location
  const isAdmin = authCtx?.authLocations?.find(
    al => al.measurement === locationData?.measurement
  )?.isAdmin === true;

  // Image selector state
  const sensorImageIds = location.sensorImageIds || [];
  const s3BasePath = locationData?.s3BasePath || '';
  const s3Bucket = locationData?.s3Bucket || 'venue-queues-images';
  const bucketUrl = `https://${s3Bucket}.s3.us-east-2.amazonaws.com`;

  const [selectedSensorId, setSelectedSensorId] = useState(null);
  const [imageUpdateLoading, setImageUpdateLoading] = useState(false);
  const [imageUpdateSuccess, setImageUpdateSuccess] = useState(false);
  const [imageTimestamp, setImageTimestamp] = useState(Date.now());

  const [failedSensorImages, setFailedSensorImages] = useState(new Set());
  const [activeImageFailed, setActiveImageFailed] = useState(false);

  useEffect(() => {
    if (!isToday) return;
    const interval = setInterval(() => {
      setImageTimestamp(Date.now());
    }, 60000);
    return () => clearInterval(interval);
  }, [isToday]);

  useEffect(() => {
    setFailedSensorImages(new Set());
    setActiveImageFailed(false);
  }, [imageTimestamp]);

  const getSensorImageUrl = (sensorId) => {
    return `${bucketUrl}/${s3BasePath}/sensors/location_${location.locationId}/sensor_${sensorId}.jpg?t=${imageTimestamp}`;
  };

  const getLocationImageUrl = () => {
    return `${bucketUrl}/${s3BasePath}/locations/location_${location.locationId}/image.jpg?t=${imageTimestamp}`;
  };

  const handleSetLocationImage = useCallback((sensorId) => {
    setImageUpdateLoading(true);
    setImageUpdateSuccess(false);

    getQueuesAPI("update-golf-event-location-image",
      {
        measurement: locationData.measurement,
        org: authCtx.org,
        locationId: location.locationId,
        sensorImageId: sensorId,
        s3BasePath: s3BasePath
      },
      (data, error) => {
        setImageUpdateLoading(false);
        if (error) {
          console.error('Failed to update location image:', error);
        } else {
          setSelectedSensorId(sensorId);
          setImageUpdateSuccess(true);
          setImageTimestamp(Date.now());
          setTimeout(() => setImageUpdateSuccess(false), 3000);
        }
      },
      authCtx.sessionToken
    );
  }, [locationData, authCtx, location, s3BasePath]);

  return (
    <div
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "20px",
          width: "90%",
          maxWidth: "900px",
          maxHeight: "85vh",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ maxHeight: "85vh", overflow: "auto", padding: "32px" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <div>
            <div style={{ fontSize: "24px", fontWeight: "700", color: "#333" }}>
              {location.name}
            </div>
            <div style={{ fontSize: "14px", color: "#888", textTransform: "capitalize" }}>
              {location.type}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#999",
              padding: "4px 8px"
            }}
          >
            &times;
          </button>
        </div>

        {/* ============ GATE LIVE SUMMARY ============ */}
        {isToday && currentData && isGate && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "20px",
            marginBottom: "24px"
          }}>
            <div style={{
              padding: "20px",
              borderRadius: "12px",
              backgroundColor: "#f8f9fa",
              textAlign: "center"
            }}>
              <div style={{ fontSize: "13px", color: "#888", marginBottom: "4px" }}>Total Exits</div>
              <div style={{ fontSize: "32px", fontWeight: "700", color: "#333" }}>
                {(currentData?.countOut || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: "14px", color: "#666" }}>Cumulative exits</div>
            </div>
          </div>
        )}

        {/* ============ GRANDSTAND / MARKET LIVE SUMMARY ============ */}
        {isToday && currentData && !isGate && (() => {
          const hasDwell = !!currentData?.dwellTime;
          const isMarket = location.type === "market";
          const colCount = isMarket ? (hasDwell ? 3 : 2) : 1;

          return (
            <div style={{
              display: "grid",
              gridTemplateColumns: `repeat(${colCount}, 1fr)`,
              gap: "20px",
              marginBottom: "24px"
            }}>
              <div style={{
                padding: "20px",
                borderRadius: "12px",
                backgroundColor: "#f8f9fa",
                textAlign: "center"
              }}>
                <div style={{ fontSize: "13px", color: "#888", marginBottom: "4px" }}>Occupancy</div>
                <div style={{ fontSize: "32px", fontWeight: "700", color: "#333" }}>
                  {percentDisplay}%
                </div>
                <div style={{ fontSize: "14px", color: "#666" }}>
                  {value.toLocaleString()} / {maxValue.toLocaleString()}
                </div>
              </div>

              {isMarket && (
                <div style={{
                  padding: "20px",
                  borderRadius: "12px",
                  backgroundColor: "#f8f9fa",
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "13px", color: "#888", marginBottom: "4px" }}>Throughput</div>
                  <div style={{ fontSize: "32px", fontWeight: "700", color: "#333" }}>
                    {(currentData?.throughputCount || 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: "14px", color: "#666" }}>total entries</div>
                </div>
              )}

              {hasDwell && (
                <div style={{
                  padding: "20px",
                  borderRadius: "12px",
                  backgroundColor: "#f8f9fa",
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "13px", color: "#888", marginBottom: "4px" }}>
                    Average Dwell Time
                  </div>
                  <div style={{ fontSize: "32px", fontWeight: "700", color: "#333" }}>
                    {Math.floor(currentData.dwellTime.time / 60)}m {currentData.dwellTime.time % 60}s
                  </div>
                  <div style={{ fontSize: "14px", color: "#666" }}>{currentData.dwellTime.area}</div>
                </div>
              )}

            </div>
          );
        })()}

        {/* ============ GATE HISTORICAL SUMMARY ============ */}
        {!isToday && dailyData && !detailLoading && isGate && (() => {
          const thruData = dailyData.throughputData || [];
          const totalExits = thruData.length > 0
            ? thruData[thruData.length - 1].countOut
            : null;

          if (totalExits === null) return null;

          return (
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "20px",
              marginBottom: "24px"
            }}>
              <div style={{
                padding: "20px",
                borderRadius: "12px",
                backgroundColor: "#f8f9fa",
                textAlign: "center"
              }}>
                <div style={{ fontSize: "13px", color: "#888", marginBottom: "4px" }}>Total Exits</div>
                <div style={{ fontSize: "32px", fontWeight: "700", color: "#333" }}>{totalExits.toLocaleString()}</div>
                <div style={{ fontSize: "14px", color: "#666" }}>cumulative exits</div>
              </div>
            </div>
          );
        })()}

        {/* ============ GRANDSTAND / MARKET HISTORICAL SUMMARY ============ */}
        {!isToday && dailyData && !detailLoading && !isGate && (() => {
          const occData = dailyData.occupancyData || [];
          const thruData = dailyData.throughputData || [];
          const dwellData = dailyData.dwellTimeData?.data || [];

          const peakOcc = occData.length > 0
            ? occData.reduce((max, d) => d.value > max.value ? d : max, occData[0])
            : null;

          const totalThroughput = thruData.length > 0
            ? thruData[thruData.length - 1].count
            : null;

          const avgDwell = dwellData.length > 0
            ? Math.round(dwellData.reduce((sum, d) => sum + d.time, 0) / dwellData.length)
            : null;

          const statCards = [
            peakOcc && {
              label: "Peak Occupancy",
              value: `${Math.round(peakOcc.percentage * 100)}%`,
              sub: `${peakOcc.value.toLocaleString()} / ${maxValue.toLocaleString()} at ${formatTime(peakOcc.timestamp)}`
            },
            totalThroughput !== null && {
              label: "Total Throughput",
              value: totalThroughput.toLocaleString(),
              sub: "total entries"
            },
            avgDwell !== null && {
              label: "Average Dwell Time",
              value: formatDwellTime(avgDwell),
              sub: dailyData.dwellTimeData?.area || ""
            }
          ].filter(Boolean);

          if (statCards.length === 0) return null;

          return (
            <div style={{
              display: "grid",
              gridTemplateColumns: `repeat(${statCards.length}, 1fr)`,
              gap: "20px",
              marginBottom: "24px"
            }}>
              {statCards.map((card, i) => (
                <div key={i} style={{
                  padding: "20px",
                  borderRadius: "12px",
                  backgroundColor: "#f8f9fa",
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "13px", color: "#888", marginBottom: "4px" }}>{card.label}</div>
                  <div style={{ fontSize: "32px", fontWeight: "700", color: "#333" }}>{card.value}</div>
                  <div style={{ fontSize: "14px", color: "#666" }}>{card.sub}</div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Occupancy Buffer Control — live days only, admin only, non-gate only */}
        {isToday && isAdmin && !isGate && (
          <OccupancyBufferControl
            location={location}
            locationData={locationData}
            authCtx={authCtx}
          />
        )}

        {/* Charts Section */}
        <div style={{
          borderRadius: "12px",
          backgroundColor: "#f8f9fa",
          padding: "24px",
          marginBottom: "24px"
        }}>
          {detailLoading ? (
            <div style={{ textAlign: "center", color: "#888", padding: "40px 0", fontSize: "16px" }}>
              Loading data...
            </div>
          ) : dailyData ? (
            <>
              {/* Occupancy chart — grandstands and markets only */}
              {!isGate && (
                <>
                  {dailyData.occupancyData?.length > 0 ? (
                    <OccupancyChart data={dailyData.occupancyData} maxValue={maxValue} />
                  ) : (
                    <div style={{ textAlign: "center", color: "#aaa", padding: "20px 0", fontSize: "14px" }}>
                      No occupancy data recorded for this date
                    </div>
                  )}
                </>
              )}

              {/* Throughput chart — markets only */}
              {!isGate && dailyData.throughputData?.length > 0 && (
                <ThroughputChart data={dailyData.throughputData} />
              )}

              {/* Dwell time chart — markets with dwell config only */}
              {!isGate && dailyData.dwellTimeData?.data?.length > 0 && (
                <DwellTimeChart dwellTimeData={dailyData.dwellTimeData} />
              )}

              {/* Gate throughput chart */}
              {isGate && dailyData.throughputData?.length > 0 && (
                <GateThroughputChart data={dailyData.throughputData} />
              )}

              {/* Gate with no data */}
              {isGate && (!dailyData.throughputData || dailyData.throughputData.length === 0) && (
                <div style={{ textAlign: "center", color: "#aaa", padding: "20px 0", fontSize: "14px" }}>
                  No exit data recorded for this date
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", color: "#888", padding: "40px 0", fontSize: "16px" }}>
              No data available for this date
            </div>
          )}
        </div>

        {/* Image Section — live days only, non-gate only */}
        {isToday && !isGate && sensorImageIds.length > 0 && s3BasePath && (
          <div style={{
            borderRadius: "12px",
            backgroundColor: "#f8f9fa",
            padding: "24px",
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px"
            }}>
              <div>
                <div style={{ fontSize: "16px", fontWeight: "600", color: "#333" }}>
                  {isAdmin ? `Sensor Images — ${location.name}` : `Live Image — ${location.name}`}
                </div>
                {isAdmin && (
                  <div style={{ fontSize: "13px", color: "#888", marginTop: "2px" }}>
                    {sensorImageIds.length} camera{sensorImageIds.length !== 1 ? 's' : ''} available · Select an image to set as the active vendor image
                  </div>
                )}
              </div>
              {imageUpdateSuccess && (
                <div style={{
                  fontSize: "13px",
                  color: "#28a745",
                  fontWeight: "500",
                  padding: "4px 12px",
                  backgroundColor: "#d4edda",
                  borderRadius: "6px"
                }}>
                  Image updated
                </div>
              )}
            </div>

            {/* Current Active Image — visible to all users */}
            <div style={{ marginBottom: isAdmin ? "20px" : "0" }}>
              {isAdmin && (
                <div style={{ fontSize: "13px", color: "#888", marginBottom: "8px" }}>
                  Current active image (vendor-facing)
                </div>
              )}
              <div style={{
                borderRadius: "10px",
                overflow: "hidden",
                border: isAdmin ? "2px solid #e20074" : "2px solid #dee2e6",
                maxWidth: "400px"
              }}>
                {activeImageFailed ? (
                  <div style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#888",
                    fontSize: "13px",
                    backgroundColor: "#e9ecef"
                  }}>
                    No active image set
                  </div>
                ) : (
                  <img
                    src={getLocationImageUrl()}
                    alt={`${location.name} active`}
                    style={{
                      width: "100%",
                      display: "block",
                      backgroundColor: "#e9ecef"
                    }}
                    onError={() => setActiveImageFailed(true)}
                  />
                )}
              </div>
            </div>

            {/* Sensor Image Grid — admin only */}
            {isAdmin && (
            <>
            <div style={{ fontSize: "13px", color: "#888", marginBottom: "8px" }}>
              Available sensor angles
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: `repeat(auto-fill, minmax(200px, 1fr))`,
              gap: "12px"
            }}>
              {sensorImageIds.map((sensorId) => (
                <div
                  key={sensorId}
                  style={{
                    borderRadius: "10px",
                    overflow: "hidden",
                    border: selectedSensorId === sensorId
                      ? "2px solid #e20074"
                      : "2px solid #dee2e6",
                    cursor: "pointer",
                    transition: "border-color 0.2s ease, transform 0.2s ease",
                    backgroundColor: "#fff"
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {failedSensorImages.has(sensorId) ? (
                    <div style={{
                      aspectRatio: "16/9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      color: "#aaa",
                      fontSize: "13px",
                      backgroundColor: "#e9ecef",
                      padding: "30px"
                    }}>
                      Sensor {sensorId}<br/>No image available
                    </div>
                  ) : (
                    <img
                      src={getSensorImageUrl(sensorId)}
                      alt={`Sensor ${sensorId}`}
                      style={{
                        width: "100%",
                        aspectRatio: "16/9",
                        objectFit: "cover",
                        display: "block",
                        backgroundColor: "#e9ecef"
                      }}
                      onError={() => {
                        setFailedSensorImages(prev => {
                          const next = new Set(prev);
                          next.add(sensorId);
                          return next;
                        });
                      }}
                    />
                  )}
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 12px",
                    borderTop: "1px solid #eee"
                  }}>
                    <span style={{ fontSize: "13px", fontWeight: "500", color: "#555" }}>
                      Sensor {sensorId}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetLocationImage(sensorId);
                      }}
                      disabled={imageUpdateLoading}
                      style={{
                        fontSize: "12px",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        border: "none",
                        backgroundColor: selectedSensorId === sensorId ? "#e20074" : "#333",
                        color: "#fff",
                        cursor: imageUpdateLoading ? "not-allowed" : "pointer",
                        opacity: imageUpdateLoading ? 0.6 : 1,
                        transition: "background-color 0.2s ease"
                      }}
                    >
                      {imageUpdateLoading ? "..." : selectedSensorId === sensorId ? "Active" : "Set Active"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            </>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}